import React from 'react';
import AlertFilled from '@uber/base-icons/alert-filled';
import { styled, useStyletron } from 'baseui';
import { KIND as BUTTON_KIND } from 'baseui/button';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE, SIZE } from 'baseui/modal';
import { ParagraphSmall } from 'baseui/typography';
import { SEQUENCE_OVERRIDE_MODAL } from '../constants';
import type { SEQUENCE_MODAL_TYPE } from './modernised-annotation-timeline-modal/constants';
type SequenceOverrideConfirmationModalProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  startFrame: number;
  endFrame: number;
  labelTitle: string;
  isLoading?: boolean;
  type: (typeof SEQUENCE_MODAL_TYPE)[keyof typeof SEQUENCE_MODAL_TYPE];
};
const HeaderTitle = styled('div', ({
  $theme
}) => ({
  fontSize: $theme.typography.HeadingXSmall.fontSize,
  fontWeight: 600,
  margin: 0,
  color: $theme.colors.contentPrimary
}));
const ContentWrapper = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale400,
  alignItems: 'flex-start'
}));
const IconWrapper = styled('div', ({
  $theme
}) => ({
  flexShrink: 0,
  marginTop: $theme.sizing.scale100
}));
const TextContent = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.scale300
}));
const StyledParagraph = styled(ParagraphSmall, ({
  $theme
}) => ({
  lineHeight: '1.4',
  margin: 0,
  color: $theme.colors.contentPrimary
}));
const getModalOverrides = () => ({
  Dialog: {
    style: ({
      $theme
    }) => ({
      maxWidth: '500px',
      width: '90vw',
      margin: '10vh auto 0',
      borderRadius: $theme.borders.radius300
    })
  },
  DialogContainer: {
    style: {
      alignItems: 'flex-start',
      paddingTop: '10vh'
    }
  },
  Body: {
    style: ({
      $theme
    }) => ({
      padding: $theme.sizing.scale600
    })
  }
});
const getCancelButtonOverrides = () => ({
  BaseButton: {
    style: ({
      $theme
    }) => ({
      marginRight: $theme.sizing.scale300,
      ':hover': {
        cursor: 'pointer'
      }
    })
  }
});
const getConfirmButtonOverrides = () => ({
  BaseButton: {
    style: ({
      $theme
    }) => ({
      backgroundColor: $theme.colors.contentPrimary,
      ':hover': {
        backgroundColor: $theme.colors.contentPrimary,
        opacity: 0.5,
        cursor: 'pointer'
      }
    })
  }
});
export const SequenceOverrideConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
  startFrame,
  endFrame,
  labelTitle,
  isLoading = false,
  type
}: SequenceOverrideConfirmationModalProps) => {
  const [, theme] = useStyletron();
  const modalConfig = SEQUENCE_OVERRIDE_MODAL[type];
  return <Modal onClose={onCancel} closeable={!isLoading} isOpen={isOpen} animate autoFocus size={SIZE.default} role={ROLE.dialog} overrides={getModalOverrides()}>
      <ModalHeader>
        <HeaderTitle>{modalConfig.TITLE}</HeaderTitle>
      </ModalHeader>
      <ModalBody>
        <ContentWrapper>
          <IconWrapper>
            <AlertFilled size={SEQUENCE_OVERRIDE_MODAL.ICON.SIZE} color={theme.colors.warning} />
          </IconWrapper>
          <TextContent>
            <StyledParagraph>
              Any manual changes made to frames <strong>{startFrame + 1}</strong> to{' '}
              <strong>{endFrame + 1}</strong> for <strong>{labelTitle}</strong> will be overridden.
              Do you want to continue?
            </StyledParagraph>
          </TextContent>
        </ContentWrapper>
      </ModalBody>
      <ModalFooter>
        <ModalButton kind={BUTTON_KIND.tertiary} size="compact" onClick={onCancel} disabled={isLoading} overrides={getCancelButtonOverrides()}>
          {SEQUENCE_OVERRIDE_MODAL.CANCEL_BUTTON}
        </ModalButton>
        <ModalButton size="compact" onClick={onConfirm} disabled={isLoading} isLoading={isLoading} overrides={getConfirmButtonOverrides()}>
          {modalConfig.CONFIRM_BUTTON}
        </ModalButton>
      </ModalFooter>
    </Modal>;
};